import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../shared/utils";

const api = axios.create({ baseURL: API });

const LABELS = ["", "Muito ruim", "Ruim", "Regular", "Bom", "Excelente"];
const EMOJIS = ["", "😞", "😕", "😐", "🙂", "😄"];
const COLORS = ["", "#ef4444", "#f97316", "#f59e0b", "#22c55e", "#16a34a"];

export default function CsatPublicPage({ token }) {
  const [step, setStep] = useState("loading"); // loading | form | done | error | rated
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/csat/${token}`)
      .then((res) => {
        if (res.data.already_rated) { setStep("rated"); return; }
        setInfo(res.data);
        setStep("form");
      })
      .catch(() => setStep("error"));
  }, [token]);

  async function submit() {
    if (!rating) return;
    setSubmitting(true);
    try {
      await api.post(`/public/csat/${token}/rate`, { rating, comment: comment || null });
      setStep("done");
    } catch {
      setStep("error");
    } finally {
      setSubmitting(false);
    }
  }

  const active = hovered || rating;

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, #0a0e14 0%, #111820 100%)",
      padding: 20, fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      <div style={{
        background: "#1a2130", border: "1px solid rgba(255,255,255,.08)",
        borderRadius: 20, padding: "40px 32px", maxWidth: 420, width: "100%",
        boxShadow: "0 24px 80px rgba(0,0,0,.5)", textAlign: "center",
      }}>
        {/* Logo */}
        <div style={{ marginBottom: 24 }}>
          <svg width="40" height="40" viewBox="0 0 34 34" fill="none" style={{ margin: "0 auto 8px", display: "block" }}>
            <defs>
              <linearGradient id="csat-g" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#3DEB7E" />
                <stop offset="100%" stopColor="#1FB855" />
              </linearGradient>
            </defs>
            <rect x="1" y="1" width="32" height="32" rx="8" stroke="url(#csat-g)" strokeWidth="2" fill="none" />
            <path d="M11 9L23 9L13 19L21 19L11 25" stroke="url(#csat-g)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
          <span style={{ color: "#25d366", fontWeight: 700, fontSize: 15, letterSpacing: "-.2px" }}>Safe Zap</span>
        </div>

        {step === "loading" && (
          <p style={{ color: "#94a3b8", fontSize: 14 }}>Carregando avaliação…</p>
        )}

        {step === "error" && (
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>❌</div>
            <h2 style={{ color: "#f1f5f9", margin: "0 0 8px", fontSize: 20 }}>Link inválido</h2>
            <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6 }}>
              Este link de avaliação não existe ou já expirou.
            </p>
          </>
        )}

        {step === "rated" && (
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h2 style={{ color: "#f1f5f9", margin: "0 0 8px", fontSize: 20 }}>Já avaliado!</h2>
            <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6 }}>
              Este atendimento já foi avaliado. Obrigado pelo feedback!
            </p>
          </>
        )}

        {step === "done" && (
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🙏</div>
            <h2 style={{ color: "#f1f5f9", margin: "0 0 8px", fontSize: 20 }}>Obrigado!</h2>
            <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6 }}>
              Sua avaliação foi registrada. Ela nos ajuda a melhorar cada vez mais!
            </p>
            <div style={{
              marginTop: 20, padding: "16px", borderRadius: 12,
              background: "rgba(37,211,102,.08)", border: "1px solid rgba(37,211,102,.2)",
            }}>
              <span style={{ fontSize: 32 }}>{EMOJIS[rating]}</span>
              <p style={{ color: COLORS[rating], fontWeight: 700, margin: "8px 0 0", fontSize: 14 }}>
                {LABELS[rating]}
              </p>
            </div>
          </>
        )}

        {step === "form" && info && (
          <>
            <h2 style={{ color: "#f1f5f9", margin: "0 0 6px", fontSize: 20, fontWeight: 700 }}>
              Como foi o atendimento?
            </h2>
            <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6, margin: "0 0 28px" }}>
              Olá, <strong style={{ color: "#f1f5f9" }}>{info.contact_name?.split(" ")[0] || "cliente"}</strong>!
              Avalie o atendimento de <strong style={{ color: "#f1f5f9" }}>{info.company_name}</strong>.
            </p>

            {/* Star rating */}
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 12 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onMouseEnter={() => setHovered(n)}
                  onMouseLeave={() => setHovered(0)}
                  onClick={() => setRating(n)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 40, lineHeight: 1, padding: "4px",
                    color: n <= active ? COLORS[active] : "#334155",
                    transform: n === active ? "scale(1.2)" : "scale(1)",
                    transition: "all .15s ease",
                    filter: n <= active ? "drop-shadow(0 0 8px " + COLORS[active] + "66)" : "none",
                  }}
                >
                  ★
                </button>
              ))}
            </div>

            {active > 0 && (
              <p style={{ color: COLORS[active], fontWeight: 600, fontSize: 14, margin: "0 0 20px" }}>
                {EMOJIS[active]} {LABELS[active]}
              </p>
            )}

            {/* Optional comment */}
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Deixe um comentário opcional…"
              rows={3}
              style={{
                width: "100%", boxSizing: "border-box",
                background: "#0f1520", border: "1px solid rgba(255,255,255,.1)",
                borderRadius: 10, color: "#f1f5f9", padding: "10px 12px",
                fontSize: 13, resize: "vertical", outline: "none",
                fontFamily: "inherit", marginBottom: 20,
              }}
            />

            <button
              onClick={submit}
              disabled={!rating || submitting}
              style={{
                width: "100%", padding: "14px", borderRadius: 10, border: "none",
                background: rating ? "#25d366" : "#1e293b",
                color: rating ? "#fff" : "#64748b",
                fontWeight: 700, fontSize: 14, cursor: rating ? "pointer" : "not-allowed",
                transition: "all .2s ease",
              }}
            >
              {submitting ? "Enviando…" : "Enviar avaliação"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
